/* SPECVLVM — Legal · Terms of Service + Refund Policy
   Ordo Semeion · MMXXVI
   NOTE: This is plain-language commercial copy, not legal advice. Have a
   lawyer review before relying on it in a regulated market. */

const SUPPORT_EMAIL = "initiate@ra999.info";
const LEGAL_UPDATED = "June 2026";

function LegalScreen({ go, section = "terms" }) {
  const [tab, setTab] = React.useState(section);

  return (
    <div className="legal-root">
      <div className="legal-head">
        <div className="label" style={{ marginBottom: 10 }}>§ — · Ordo Semeion · Field Instrument №01</div>
        <h1 className="legal-title">{tab === "terms" ? "Terms of Service" : "Refund Policy"}</h1>
        <div className="legal-tabs">
          <button className={`legal-tab ${tab === "terms" ? "on" : ""}`} onClick={() => setTab("terms")}>Terms of Service</button>
          <button className={`legal-tab ${tab === "refund" ? "on" : ""}`} onClick={() => setTab("refund")}>Refund Policy</button>
        </div>
        <div className="legal-meta lore-mono lore-mono--faint">Last updated · {LEGAL_UPDATED}</div>
      </div>

      {tab === "terms" && (
        <div className="legal-body">
          <p className="legal-lead">
            These Terms govern your use of SPECVLVM · Auditus (the “Instrument”), operated by
            Ordo Semeion (“we”, “us”). By creating an account, purchasing credits, or submitting
            a specimen for examination, you agree to these Terms.
          </p>

          <LegalSection n="1" title="What the Instrument does">
            <p>SPECVLVM performs an automated semiotic examination of text you submit (a “specimen”).
            It produces analytical findings and an ethical verdict against the Charter §§I–VI. The
            output is an interpretive, AI-generated analysis offered for study and reflection. It is
            not professional, legal, financial, medical, or psychological advice, and must not be
            relied upon as a statement of fact about any person or organisation.</p>
          </LegalSection>

          <LegalSection n="2" title="Accounts">
            <p>You must provide a valid email and keep your credentials secure. You are responsible
            for activity under your account. You must be at least 16 years old (or the age of digital
            consent in your jurisdiction) to use the Instrument.</p>
          </LegalSection>

          <LegalSection n="3" title="Examination credits">
            <p>Access to examinations is sold as one-time credit packs. One credit funds one full
            examination (findings and verdict). Credits do not expire. Credits carry no cash value,
            are not transferable between accounts, and cannot be exchanged for money except as set
            out in the Refund Policy.</p>
            <p>Anonymous, signed-out use is offered with a limited number of free examinations and may
            be changed or withdrawn at any time.</p>
          </LegalSection>

          <LegalSection n="4" title="Payment">
            <p>Payments are processed by our payment provider (Stripe). We do not receive or store
            your full card details. Prices are shown at checkout in US dollars and may change; changes
            do not affect credits already purchased.</p>
          </LegalSection>

          <LegalSection n="5" title="Your content and privacy">
            <p>You retain ownership of the specimens you submit. We do not retain specimen text unless
            you explicitly save an examination to your account; saved examinations and their text are
            stored so you can return to them, and are deleted permanently when you delete them or your
            account. Specimen text is transmitted to our AI processing provider solely to produce your
            findings. See the in-app Codex (Appendix F) for the full data summary.</p>
            <p>Do not submit content that is unlawful, that you have no right to share, or that contains
            others’ sensitive personal data.</p>
          </LegalSection>

          <LegalSection n="6" title="Acceptable use">
            <p>You agree not to misuse the Instrument, including: attempting to break, overload, or
            reverse-engineer it; bypassing credit or rate limits; using it to harass, defame, or harm
            others; or reselling its output as authoritative fact. We may suspend accounts that abuse
            the service.</p>
          </LegalSection>

          <LegalSection n="7" title="Availability and changes">
            <p>The Instrument is provided “as is” and “as available”. We may modify, suspend, or
            discontinue features at any time. We aim for high availability but do not guarantee
            uninterrupted service.</p>
          </LegalSection>

          <LegalSection n="8" title="Limitation of liability">
            <p>To the maximum extent permitted by law, Ordo Semeion is not liable for indirect or
            consequential losses, or for decisions made in reliance on examination output. Our total
            liability for any claim is limited to the amount you paid for credits in the preceding
            three months.</p>
          </LegalSection>

          <LegalSection n="9" title="Contact">
            <p>Questions about these Terms: <a className="legal-link" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></p>
          </LegalSection>
        </div>
      )}

      {tab === "refund" && (
        <div className="legal-body">
          <p className="legal-lead">
            We want the Instrument to be worth its price. This policy explains when examination
            credits can be refunded.
          </p>

          <LegalSection n="1" title="Digital goods">
            <p>Examination credits are digital goods delivered to your account immediately on purchase.
            Because credits can be used the moment they are issued, purchases are generally
            non-refundable once credits have been spent.</p>
          </LegalSection>

          <LegalSection n="2" title="When we will refund">
            <p>We will refund a credit pack if:</p>
            <ul className="legal-list">
              <li>You were charged but credits were not added to your account, and we cannot resolve it;</li>
              <li>You were charged more than once for the same purchase (we refund the duplicate);</li>
              <li>A technical fault on our side prevented the examinations you paid for from running, and the credits remain unused.</li>
            </ul>
            <p>In these cases we refund the unused portion to your original payment method.</p>
          </LegalSection>

          <LegalSection n="3" title="Unused packs">
            <p>If you have purchased a pack within the last 14 days and have <em>not used any</em> of its
            credits, you may request a full refund of that pack. Once any credit from a pack has been
            spent, that pack is no longer eligible for a discretionary refund.</p>
          </LegalSection>

          <LegalSection n="4" title="What is not refundable">
            <p>We do not refund credits that have already been spent on examinations, dissatisfaction
            with the interpretive content of an examination (the output is inherently subjective), or
            charges older than 90 days.</p>
          </LegalSection>

          <LegalSection n="5" title="Statutory rights">
            <p>Nothing in this policy removes consumer rights you have under the law of your country.
            If your local law grants stronger refund rights for digital goods, those apply.</p>
          </LegalSection>

          <LegalSection n="6" title="How to request a refund">
            <p>Email <a className="legal-link" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> from the
            address on your account, with the approximate date and amount of the purchase. We aim to
            respond within 3–5 business days.</p>
          </LegalSection>
        </div>
      )}

      <div className="legal-footer">
        <button className="cta-ghost" onClick={() => go("bench")}>← Return to the Bench</button>
        <a className="legal-link" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
      </div>
    </div>
  );
}

function LegalSection({ n, title, children }) {
  return (
    <section className="legal-section">
      <h2 className="legal-section-title"><span className="legal-section-num">{n}.</span> {title}</h2>
      <div className="legal-section-body">{children}</div>
    </section>
  );
}

/* Expose to global scope (per-file build) */
Object.assign(window, { LegalScreen, SUPPORT_EMAIL });
